import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {BranchModel} from '../models/BranchModel'
import {filterDataChange, getAll, updateBranchStatus} from '../redux/BranchRedux'
import {PaginateModel} from '../../../shared/models/PaginateModel'
import Paginator from '../../../components/Paginator'
import moment from 'moment'
import Tooltip from 'react-tooltip-lite'
import {selectBranchFilter, selectBranchList} from '../redux/BranchRedux'
import {BranchStatus} from '../constants'
import Form from 'react-bootstrap/Form'
import DeactivateBranchModal from './DeactivateBranchModal'
import ActivateBranchModal from './ActivateBranchModal'

type Props = {
  className?: string
}

const BranchTable: FC<Props> = (props) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const didRequest = useRef(false)
  const data: BranchModel[] = useAppSelector(selectBranchList)
  const filter: PaginateModel = useAppSelector(selectBranchFilter)
  
  // Modal state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false)
  const [branchToDeactivate, setBranchToDeactivate] = useState<BranchModel | null>(null)
  const [showActivateModal, setShowActivateModal] = useState(false)
  const [branchToActivate, setBranchToActivate] = useState<BranchModel | null>(null)

  useEffect(() => {
    if (!didRequest.current) {
      dispatch(getAll())
      didRequest.current = true
    }
  }, [dispatch])

  const handlePageChanged = (targetPage: number) => {
    dispatch(filterDataChange({...filter, page: targetPage}))
  }

  const handleDeactivateBranch = (branch: BranchModel) => {
    setBranchToDeactivate(branch)
    setShowDeactivateModal(true)
  }

  const handleActivateBranch = (branch: BranchModel) => {
    setBranchToActivate(branch)
    setShowActivateModal(true)
  }

  const handleCloseModal = () => {
    setShowDeactivateModal(false)
    setBranchToDeactivate(null)
    setShowActivateModal(false)
    setBranchToActivate(null)
  }

  const handleConfirmDeactivate = (transferToBranchId: number) => {
    if (branchToDeactivate) {
      // Use the unified updateBranchStatus action with data transfer
      dispatch(updateBranchStatus({
        id: branchToDeactivate.id, 
        status: BranchStatus.INACTIVE,
        targetBranchId: transferToBranchId
      }))
    }
    handleCloseModal()
  }

  const handleConfirmActivate = (personInChargeId: number) => {
    if (branchToActivate) {
      // Use the unified updateBranchStatus action with person in charge assignment
      dispatch(updateBranchStatus({
        id: branchToActivate.id, 
        status: BranchStatus.ACTIVE,
        targetPersonInChargeId: personInChargeId
      }))
    }
    handleCloseModal()
  }

  const renderItemActions = (item: BranchModel) => {
    let actions = []

    if (item.status === BranchStatus.ACTIVE) {
      actions = [
        <Tooltip
          key={`status-${item.id}`}
          content={<div>{intl.formatMessage({id: 'Scheduler.STATUS.INACTIVE'})}</div>}
          className='tooltips'
        >
          <button
            key='status'
            className='btn btn-icon btn-light-danger btn-sm'
            onClick={() => handleDeactivateBranch(item)}
          >
            <i className='bi bi-x-circle fs-4'></i>
          </button>
        </Tooltip>,
      ]
    } else {
      actions = [
        <Tooltip
          key={`status-${item.id}`}
          content={<div>{intl.formatMessage({id: 'Scheduler.STATUS.ACTIVE'})}</div>}
          className='tooltips'
        >
          <button
            key='status'
            className='btn btn-icon btn-light-success btn-sm'
            onClick={() => handleActivateBranch(item)}
          >
            <i className='bi bi-check-circle fs-4'></i>
          </button>
        </Tooltip>,
      ]
    }

    return <div className='d-flex justify-content-end flex-shrink-0'>{actions}</div>
  }

  const renderSchedulerItem = (item: BranchModel) => {
    return (
      <tr key={item.id}>
        <td> {item.id.toString()}</td>
        <td> {item.name} </td>
        <td> {item.address} </td>
        <td> {item.phone} </td>
        <td> {item.perInCharge} </td>
        <td>
          <Form.Check
            type='switch'
            id={`isSeparateBillingSwitch-${item.id}`}
            defaultChecked={item.isSeparateBilling}
            readOnly={true}
            disabled={true}
            label={''}
          />
        </td>
        <td>
          <span
            className={`badge badge-light-${
              item.status === BranchStatus.ACTIVE ? 'success' : 'danger'
            } d-inline-flex align-items-center`}
          >
            {intl.formatMessage({id: 'BRANCH.STATUS.' + item.status})}
          </span>
        </td>
        <td> {moment(item.createdAt).format(process.env.REACT_APP_DATE_FORMAT)} </td>
        <td>{renderItemActions(item)}</td>
      </tr>
    )
  }
  return (
    <>
      <div className='table-responsive'>
        {/* begin::Table */}
        <table className='table table-hover table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
          {/* begin::Table head */}
          <thead>
            <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
              <th>{intl.formatMessage({id: 'BRANCH.TABLE.ID'})}</th>
              <th>{intl.formatMessage({id: 'BRANCH.TABLE.NAME'})}</th>
              <th>{intl.formatMessage({id: 'BRANCH.TABLE.ADDRESS'})}</th>
              <th>{intl.formatMessage({id: 'BRANCH.TABLE.PHONE'})}</th>
              <th>{intl.formatMessage({id: 'BRANCH.TABLE.PER_IN_CHARGE'})}</th>
              <th>{intl.formatMessage({id: 'BRANCH.TABLE.IS_SEPARATE_BILLING'})}</th>
              <th>{intl.formatMessage({id: 'BRANCH.TABLE.STATUS'})}</th>
              <th>{intl.formatMessage({id: 'BRANCH.TABLE.CREATE_AT'})}</th>
              <th className='text-end'>{intl.formatMessage({id: 'BRANCH.TABLE.ACTION'})}</th>
            </tr>
          </thead>
          {/* end::Table head */}
          {/* begin::Table body */}
          <tbody>{data?.map((item: any) => renderSchedulerItem(item))}</tbody>
          {/* end::Table body */}
        </table>
        {/* end::Table */}
      </div>
      <div className='d-flex justify-content-end pt-10'>
        <Paginator
          currentPage={filter.page}
          totalPages={filter.pageCount || 1}
          onPageChange={handlePageChanged}
        />
      </div>

      {/* Deactivate Branch Modal */}
      <DeactivateBranchModal
        isShow={showDeactivateModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDeactivate}
        currentBranch={branchToDeactivate}
        availableBranches={data}
      />

      {/* Activate Branch Modal */}
      <ActivateBranchModal
        isShow={showActivateModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmActivate}
        currentBranch={branchToActivate}
      />
    </>
  )
}

export default BranchTable
